import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/session";

const emptyForm = { category: CATEGORIES[0], subcategory: "", title: "", body: "", default_duration_minutes: 5 };

export function MinistryLibraryPanel({ onSelect, search }) {
  const [ministries, setMinistries] = useState(null);
  const [ministryId, setMinistryId] = useState(null);
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/orgs/my-ministries").then(({ data }) => {
      setMinistries(data);
      if (data.length > 0) setMinistryId((prev) => prev || data[0].id);
    }).catch(() => setMinistries([]));
  }, []);

  const loadItems = () => {
    if (!ministryId) return;
    api.get("/ministry-library", { params: { ministry_id: ministryId } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };

  useEffect(() => { loadItems(); }, [ministryId]);

  const filtered = useMemo(
    () => items.filter((i) => i.category === activeCategory && i.title.toLowerCase().includes((search || "").toLowerCase())),
    [items, activeCategory, search]
  );

  const openCreate = () => {
    setEditingItem(null);
    setForm({ ...emptyForm, category: activeCategory });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({ category: item.category, subcategory: item.subcategory || "", title: item.title, body: item.body, default_duration_minutes: item.default_duration_minutes });
    setFormOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/ministry-library/${editingItem.id}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/ministry-library", { ministry_id: ministryId, ...form });
        toast.success("Added to ministry library");
      }
      setFormOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from your ministry library?`)) return;
    try {
      await api.delete(`/ministry-library/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Item deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete item");
    }
  };

  if (ministries === null) {
    return <p className="text-sm text-muted-foreground text-center py-6">Loading ministry library...</p>;
  }

  if (ministries.length === 0) {
    return (
      <div className="text-center py-8" data-testid="ministry-panel-empty-state">
        <Users className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">You're not part of a ministry yet.</p>
        <p className="text-xs text-muted-foreground mt-1">Join or create one from "My Ministry" to build a private library.</p>
      </div>
    );
  }

  return (
    <div data-testid="ministry-library-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {ministries.length > 1 && (
          <Select value={ministryId} onValueChange={setMinistryId}>
            <SelectTrigger className="h-8 w-48 text-sm" data-testid="ministry-panel-ministry-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ministries.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs px-2 py-1 rounded-full border transition-colors ${activeCategory === cat ? "bg-[var(--brand-navy)] text-white border-[var(--brand-navy)]" : "border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] hover:bg-[var(--brand-bg)] dark:hover:bg-[var(--brand-bg-dark)]"}`}
              data-testid={`ministry-panel-category-${cat.replace("/", "-")}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1 min-h-[80px]" data-testid="ministry-panel-items">
        {filtered.length === 0 && (
          <p className="col-span-full text-sm text-muted-foreground text-center py-6">No {activeCategory.toLowerCase()} yet in this ministry's library.</p>
        )}
        {filtered.map((item) => (
          <div key={item.id} className="flex items-center gap-1 group">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onSelect(item)}
                  className="flex-1 text-left text-sm font-medium px-2 py-1.5 rounded-md hover:bg-[var(--brand-gold)]/20 transition-colors truncate"
                  data-testid={`ministry-item-${item.id}`}
                >
                  {item.title}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs whitespace-normal">
                {item.body ? item.body.slice(0, 140) + (item.body.length > 140 ? "..." : "") : "No preview available"}
              </TooltipContent>
            </Tooltip>
            <button onClick={() => openEdit(item)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground shrink-0" data-testid={`ministry-item-edit-${item.id}`}>
              <Pencil className="h-3 w-3" />
            </button>
            <button onClick={() => handleDelete(item)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[var(--brand-crimson)] shrink-0" data-testid={`ministry-item-delete-${item.id}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      <Popover open={formOpen} onOpenChange={setFormOpen}>
        <PopoverTrigger asChild>
          <button
            onClick={openCreate}
            className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-[var(--brand-gold-text)] dark:text-amber-300 hover:underline px-2 py-2.5 border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] mt-1"
            data-testid="ministry-panel-add-trigger"
          >
            <Plus className="h-3.5 w-3.5" /> New ministry item
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-2" data-testid="ministry-panel-add-popover">
          <p className="text-sm font-semibold">{editingItem ? "Edit item" : "Add to ministry library"}</p>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="h-8 text-sm" data-testid="ministry-item-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Subcategory (optional)" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="ministry-item-subcategory-input" />
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ministry-item-title-input" />
          <Textarea placeholder="Text..." rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ministry-item-body-input" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground shrink-0">Minutes</label>
            <Input type="number" min={1} value={form.default_duration_minutes} onChange={(e) => setForm({ ...form, default_duration_minutes: Number(e.target.value) || 1 })} className="h-8" data-testid="ministry-item-duration-input" />
          </div>
          <Button onClick={submit} disabled={saving || !form.title.trim()} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="ministry-item-submit-button">
            {saving ? "Saving..." : editingItem ? "Save changes" : "Add item"}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
